import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Stack, Typography, createFilterOptions } from '@mui/material';

// types
import { User, PPF_USER_TYPE_OPTIONS } from 'types/user';
import { useGetUserLookupList } from 'api/user';
import UserModal from './UserModal';
import { useGetPermissions } from 'api/authUser';
import { hasAddPermission, ensureCanAdd } from 'utils/permissions';
import { useNavigate } from 'react-router-dom';
import { FormikContext } from 'formik';
import Lookup from 'components/controls/Lookup';
import useAuth from 'hooks/useAuth';

interface UserLookupProps {
  name: string;
  value?: number | number[] | null;
  onChange?: (value: number | number[] | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  loading?: boolean;
  onClear?: () => void;
  showClearButton?: boolean;
  showAddButton?: boolean;
  multiple?: boolean;
  onUserCreated?: (newUser: User) => void;
  includeAll?: boolean;
  onUserSelected?: (newUser: User[] | User | null) => void;
  excludeID?: number | number[];
}

const getUserTypeLabel = (userOption: User): string => {
  if (!userOption) return '';

  const u = userOption as any;

  if (u.userTypeName) return u.userTypeName;
  if (u.userRoleNames) return u.userRoleNames;
  if (u.roleName) return u.roleName;
  if (u.role) return u.role;

  const rawType = u.userType ?? u.ppfUserType;
  if (typeof rawType === 'number' && rawType > 0) {
    const matched = PPF_USER_TYPE_OPTIONS.filter((o) => (rawType & o.value) !== 0).map((o) => o.userType);
    if (matched.length > 0) {
      return matched.join(', ');
    }
  }

  if (typeof rawType === 'string') return rawType;

  return '';
};

const filterUserOptions = createFilterOptions<User>({
  stringify: (option: User) => {
    const typeLabel = getUserTypeLabel(option);
    const u = option as any;
    const rawType = u.ppfUserType ?? u.userType ?? '';
    return `${option.fullName || ''} ${typeLabel} ${rawType}`;
  }
});

const UserLookup = ({
  name,
  value,
  onChange,
  label = 'User',
  placeholder = 'Select User',
  disabled = false,
  required = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  multiple = false,
  loading = false,
  onClear,
  showClearButton = true,
  showAddButton = true,
  onUserCreated,
  onUserSelected,
  includeAll = false,
  excludeID
}: UserLookupProps) => {
  const formik = React.useContext(FormikContext);
  const [openModal, setOpenModal] = useState(false);
  const { user } = useAuth();

  const { data: rawUsers, isLoading, mutate: mutateUserLookupList } = useGetUserLookupList();

  const users = useMemo(() => {
    if (!rawUsers) return [];
    let users = rawUsers;
    if (excludeID !== undefined && excludeID !== null) {
      const excludeIDs = (Array.isArray(excludeID) ? excludeID : [excludeID])
        .filter((id) => id !== undefined && id !== null)
        .map((id) => Number(id));
      if (excludeIDs.length > 0) {
        users = users.filter((u: User) => !excludeIDs.includes(Number(u.userID)));
      }
    }
    return includeAll ? [{ userID: -1, fullName: 'All' } as User, ...users] : users;
  }, [rawUsers, includeAll, excludeID]);
  const { permissions } = useGetPermissions();
  const navigate = useNavigate();

  const featureId = 1; // Users
  const allowAdd = hasAddPermission(featureId, permissions);

  const selectedUser = useMemo(() => {
    if (!users?.length) return multiple ? [] : null;

    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        return users.filter((u: User) => value.includes(u.userID));
      }
      return [];
    } else {
      if (value !== null && value !== undefined) {
        return users.find((u: User) => u.userID === value) || null;
      }
      return null;
    }
  }, [value, users, multiple]);

  const prevSelectedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!onUserSelected || isLoading) return;
    const serialized = JSON.stringify(selectedUser);
    if (prevSelectedRef.current !== undefined && prevSelectedRef.current !== serialized) {
      onUserSelected(selectedUser);
    }
    prevSelectedRef.current = serialized;
  }, [selectedUser, onUserSelected, isLoading]);

  const handleAddUser = async () => {
    const ok = await ensureCanAdd(featureId, (path, opts) => navigate(path, opts));
    if (!ok) return;
    setOpenModal(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();

    const emptyValue = multiple ? [] : null;

    if (formik && typeof (formik as any).setFieldValue === 'function') {
      (formik as any).setFieldValue(name, emptyValue, true);
      (formik as any).setFieldTouched?.(name, true, true);
    }

    onChange?.(emptyValue);
    onClear?.();
  };

  const handleUserCreated = (newUser: User | User[]) => {
    const user = Array.isArray(newUser) ? newUser[0] : newUser;
    const normalizedUser: User = {
      ...user,
      userID: (user as any).userID ?? (user as any).primaryKeyID
    } as User;

    if (multiple) {
      const newSelection = [...(selectedUser as User[]), normalizedUser];
      const ids = newSelection.map((u) => u.userID);

      if (formik && typeof (formik as any).setFieldValue === 'function') {
        (formik as any).setFieldValue(name, ids, true);
        (formik as any).setFieldTouched?.(name, true, true);
      }

      onChange?.(ids);
      onUserSelected?.(newSelection);
    } else {
      const id = normalizedUser.userID;

      if (formik && typeof (formik as any).setFieldValue === 'function') {
        (formik as any).setFieldValue(name, id, true);
        (formik as any).setFieldTouched?.(name, true, true);
      }

      onChange?.(id);
      onUserSelected?.(normalizedUser);
    }
    mutateUserLookupList();
    setOpenModal(false);
  };

  const handleChange = (newValue: any) => {
    if (multiple) {
      const ids = newValue ? newValue.map((u: User) => u.userID) : [];

      if (formik && typeof (formik as any).setFieldValue === 'function') {
        (formik as any).setFieldValue(name, ids, true);
        if ((formik as any).touched?.[name]) {
          (formik as any).setFieldTouched?.(name, true, true);
        }
      }

      onChange?.(ids);
      onUserSelected?.(newValue);
    } else {
      const id = newValue ? newValue.userID : null;

      if (formik && typeof (formik as any).setFieldValue === 'function') {
        (formik as any).setFieldValue(name, id, true);
        if ((formik as any).touched?.[name]) {
          (formik as any).setFieldTouched?.(name, true, true);
        }
      }

      onChange?.(id);
      onUserSelected?.(newValue);
    }
  };

  return (
    <>
      <Lookup
        lookupID="user-lookup"
        label={label}
        placeholder={placeholder}
        size={size}
        fullWidth={fullWidth}
        showClearButton={showClearButton && (includeAll ? (value !== 0 && value !== null) : value !== null)}
        showAddButton={showAddButton}
        loading={isLoading}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        allowAdd={allowAdd}
        value={!multiple ? selectedUser : undefined}
        selectedOptions={multiple ? selectedUser : undefined}
        dataSource={users}
        addButtonTitle="Add New User"
        getOptionLabel={(option: User) => option.fullName || ''}
        isOptionEqualToValue={(option, value) => option.userID === value.userID}
        filterOptions={filterUserOptions}
        renderOption={(props, option: User) => {
          const { key, ...restProps } = props as any;
          const userTypeLabel = getUserTypeLabel(option);

          return (
            <Box
              component="li"
              key={key || option.userID}
              {...restProps}
              sx={{
                p: '8px !important',
                borderBottom: '1px solid',
                borderBottomColor: (theme) => theme.palette.divider,

                '&:last-of-type': {
                  borderBottom: 'none',
                },
              }}
            >
              <Stack>
                <Typography >
                  {option.fullName}
                </Typography>
                {userTypeLabel ? (
                  <Typography color='secondary' sx={{ fontSize: 12 }}>
                    {userTypeLabel}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        }}
        onChange={handleChange}
        onBlur={() => formik?.setFieldTouched(name, true, true)}
        handleAddNewRecord={handleAddUser}
        handleClear={(e) => {
          e.stopPropagation();
          const defaultValue = includeAll ? 0 : (multiple ? [] : null);
          if (formik) {
            formik.setFieldValue(name, defaultValue, true);
            formik.setFieldTouched(name, true, true);
          }
          onChange?.(defaultValue);
          onClear?.();
        }}
        multiple={multiple}
      />

      {openModal && <UserModal open={openModal} modalToggler={() => setOpenModal(!openModal)} user={null} currentUser={user} onUserCreated={handleUserCreated} />}
    </>
  );
};

export default UserLookup;
